import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Ladro (manuale V1, rr. 3747-3808).
 *
 * Si dividono in:
 *  - **4 mosse automatiche** (`type: 'starting'`) prese al 1° livello senza
 *    scelta: *Attacco Furtivo*, *Esperto di Trappole*, *Mano Lesta*,
 *    *Moralità Flessibile*.
 *  - **3 mosse di scelta** (`type: 'starting'`) tra cui il giocatore sceglie
 *    UNA al 1° livello: *Avvelenatore*, *Speriamo che Tenga!*, *Arte
 *    Persuasiva*. Sono raggruppate in `ClassDefinition.startingChoiceGroups`
 *    con `pickCount: 1`.
 *
 * Caso speciale: *Avvelenatore* ha sotto-struttura (4 veleni a scelta) →
 * usa il campo `choices` di `MoveSchema` con `choiceCount: 1`.
 */

const attaccoFurtivo: Move = {
  id: 'ladro.attacco-furtivo',
  name: 'Attacco Furtivo',
  type: 'starting',
  classId: 'ladro',
  trigger: "Quando attacchi con un'arma da mischia un nemico indifeso o sorpreso",
  description:
    "Puoi scegliere di infliggere il tuo dado di Danno senza bisogno di tirare, oppure cercare di ottenere uno specifico effetto e tirare+**DES**. Con 10+, scegli due opzioni distinte. Con 7-9, scegline una sola:\n\n- non entri in corpo a corpo col nemico;\n- infliggi il tuo dado di Danno +1d6;\n- ti crei un vantaggio: tu o un tuo alleato ottenete +1 al prossimo tiro contro il nemico;\n- riduci la sua Armatura di 1, finché non la ripara;\n- stordisci il tuo bersaglio (se l'avversario può essere stordito).",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Scegli due opzioni distinte dalla lista.' },
    on7to9: { text: 'Scegli una sola opzione dalla lista.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const espertoDiTrappole: Move = {
  id: 'ladro.esperto-di-trappole',
  name: 'Esperto di Trappole',
  type: 'starting',
  classId: 'ladro',
  trigger: "Quando ti prepari ad attraversare un'area pericolosa",
  description:
    "Tira+**DES**. Con 10+, prendi 3. Con 7-9, prendi 1. Mentre attraversi l'area, puoi spendere una presa per fare una delle seguenti domande:\n\n- c'è una trappola qui, e se sì, che cosa la attiva?\n- Che cosa fa la trappola quando viene attivata?\n- Che cos'altro c'è qui di nascosto?",
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Prendi 3 prese.' },
    on7to9: { text: 'Prendi 1 presa.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const manoLesta: Move = {
  id: 'ladro.mano-lesta',
  name: 'Mano Lesta',
  type: 'starting',
  classId: 'ladro',
  trigger: 'Quando scassini serrature, borseggi o disattivi trappole',
  description:
    'Tira+**DES**. Con 10+, lo fai senza problemi. Con un 7-9, lo fai comunque, ma il GM ti offrirà due opzioni tra destare un sospetto, metterti in pericolo, o pagare un costo, e tu dovrai sceglierne una.',
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Lo fai senza problemi.' },
    on7to9: {
      text: 'Lo fai comunque, ma scegli tra: destare un sospetto, metterti in pericolo, o pagare un costo.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const moralitaFlessibile: Move = {
  id: 'ladro.moralita-flessibile',
  name: 'Moralità Flessibile',
  type: 'starting',
  classId: 'ladro',
  trigger: 'Quando qualcuno prova a individuare il tuo Allineamento',
  description: "Puoi dichiarare l'Allineamento che preferisci.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const avvelenatore: Move = {
  id: 'ladro.avvelenatore',
  name: 'Avvelenatore',
  type: 'starting',
  classId: 'ladro',
  trigger: 'Alla creazione del personaggio (mossa di scelta iniziale)',
  description:
    "Sei un esperto nella preparazione e nell'uso dei veleni. Scegline uno dalla lista. Questo veleno non è più *pericoloso* per te e ne ottieni 3 utilizzi. Ogni volta che hai qualche ora di tempo e un posto sicuro per preparare il veleno che hai scelto, se hai gli ingredienti necessari (concordali con il GM), puoi crearne tre dosi.",
  tags: [],
  prerequisites: [],
  replaces: [],
  choices: [
    {
      id: 'olio-di-tagete',
      label: 'Olio di Tagete',
      description:
        'Tag *applicato*. Il bersaglio cade in un sonno leggero, da cui si sveglierà come se si fosse addormentato naturalmente.',
    },
    {
      id: 'ambrosia-rossa',
      label: 'Ambrosia Rossa',
      description:
        'Tag *a contatto*. Il bersaglio infligge -1d4 danni continuati finché non viene curato.',
    },
    {
      id: 'radiola-dorata',
      label: 'Radiola Dorata',
      description:
        'Tag *applicato*. Il bersaglio tratta la prossima creatura che vede come un alleato fidato, finché non ha prova del contrario.',
    },
    {
      id: 'lacrime-di-serpente',
      label: 'Lacrime di Serpente',
      description:
        'Tag *a contatto*. Chiunque infligga danni al bersaglio tira due volte e prende il miglior risultato.',
    },
  ],
  choiceCount: 1,
};

const speriamoCheTenga: Move = {
  id: 'ladro.speriamo-che-tenga',
  name: 'Speriamo che Tenga!',
  type: 'starting',
  classId: 'ladro',
  trigger: 'Quando ripari, riconfiguri o crei un congegno in fretta e su due piedi',
  description:
    'Descrivi cosa vuoi farci e tira+**DES**. Con 10+, funziona come progettato. Con 7-9, scegli uno:\n\n- non funziona a lungo, o è fragile e non potrai farci più di tanto;\n- funziona, ma presenta una qualche bizzarria o strana complicazione che non scoprirai prima che si attivi.',
  roll: {
    stat: 'destrezza',
    on10plus: { text: 'Funziona come progettato.' },
    on7to9: {
      text: 'Scegli uno: non funziona a lungo / è fragile, oppure funziona con bizzarria/complicazione imprevista.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const artePersuasiva: Move = {
  id: 'ladro.arte-persuasiva',
  name: 'Arte Persuasiva',
  type: 'starting',
  classId: 'ladro',
  trigger: 'Quando utilizzi bugie, chiacchiere o inganni per evitare sospetti o uscire dai guai',
  description:
    "Tira+**CAR**. Con 7-9, le tue menzogne vengono credute, almeno per ora. Con un 10+, scegli anche una tra le seguenti opzioni:\n\n- sposti l'attenzione o i sospetti altrove;\n- la tua menzogna rimarrà indiscussa fino a quando non verranno alla luce dei fatti che la smentiscono;\n- copri anche i tuoi amici.",
  roll: {
    stat: 'carisma',
    on10plus: { text: "Le menzogne sono credute e scegli un'opzione vantaggiosa." },
    on7to9: { text: 'Le menzogne sono credute, almeno per ora.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (4): prese tutte al livello 1 senza scelta.
 * Vanno in `ClassDefinition.startingMoves` (lista di ID).
 */
export const ladroStartingAutoMoves: readonly Move[] = [
  attaccoFurtivo,
  espertoDiTrappole,
  manoLesta,
  moralitaFlessibile,
] as const;

/**
 * Mosse iniziali di SCELTA (3): il giocatore ne sceglie una al livello 1.
 * Vanno in `ClassDefinition.startingChoiceGroups[0].moveOptions`.
 */
export const ladroStartingChoiceMoves: readonly Move[] = [
  avvelenatore,
  speriamoCheTenga,
  artePersuasiva,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const ladroInitialMoves: readonly Move[] = [
  ...ladroStartingAutoMoves,
  ...ladroStartingChoiceMoves,
];
